// Build-version watch (Paolo 2026-11-03 19:0x: "works on desktop but not mobile … mobile has not refreshed and is
// caching"). The servers do not cache (nginx sends index.html and the hashed bundles with no-cache, all the way through
// the public host); what goes stale is the PHONE: a home-screen / backgrounded tab resumes with the old JavaScript in
// memory, keeps polling the API and draws the new data with the old components. Nothing in the app ever reloaded it.
//
// This watch refetches the page shell (no-store) every `intervalMs` and whenever the app comes back to the foreground,
// reads the bundle hash out of it (the `/assets/index-<hash>.js` script tag) and compares it with the one running:
//   - a change seen while the app was in the BACKGROUND reloads at once (the user has just switched to the app);
//   - a change seen while the user is on the page sets `updateAvailable`, which the UpdateAvailablePill shows, and the
//     user reloads with a tap (never mid-interaction behind their back).
// Pure helpers are exported for tests; the composable takes its fetch / reload / clock as injectable arguments.
import { onMounted, onUnmounted, ref, type Ref } from 'vue'

export const BUNDLE_RE = /\/assets\/index-([A-Za-z0-9_-]+)\.js/

/** The bundle hash in a page shell (or any HTML / script URL), or null. */
export function bundleHashOf(html: string | null | undefined): string | null {
  const m = html?.match(BUNDLE_RE)
  return m ? m[1] : null
}

/** The hash of the bundle this document is running (its module script tag). */
export function runningBundleHash(doc: Document = document): string | null {
  const script = Array.from(doc.querySelectorAll('script[src]')).map(s => s.getAttribute('src') ?? '').find(src => BUNDLE_RE.test(src))
  return bundleHashOf(script)
}

export interface BuildVersionOptions {
  intervalMs?: number
  fetchShell?: () => Promise<string | null>
  reload?: () => void
  visible?: () => boolean
  now?: () => number
}

export interface BuildVersionWatch {
  updateAvailable: Ref<boolean>
  newHash: Ref<string | null>
  check: (cameToForeground?: boolean) => Promise<void>
  reload: () => void
}

export const DEFAULT_INTERVAL_MS = 5 * 60_000

export function createBuildVersionWatch(running: string | null, opts: BuildVersionOptions = {}): BuildVersionWatch {
  const fetchShell = opts.fetchShell ?? (async () => {
    try {
      const r = await fetch('/', { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } })
      return r.ok ? await r.text() : null
    } catch { return null }
  })
  const reload = opts.reload ?? (() => window.location.reload())
  const visible = opts.visible ?? (() => typeof document === 'undefined' || document.visibilityState !== 'hidden')
  const updateAvailable = ref(false)
  const newHash = ref<string | null>(null)
  let busy = false
  async function check(cameToForeground = false) {
    if (busy || !running) return
    busy = true
    try {
      const served = bundleHashOf(await fetchShell())
      if (!served || served === running) return
      newHash.value = served
      if (cameToForeground || !visible()) { reload(); return }
      updateAvailable.value = true
    } finally { busy = false }
  }
  return { updateAvailable, newHash, check, reload }
}

/** Mount the watch on the app shell: interval + visibilitychange, torn down on unmount. */
export function useBuildVersion(opts: BuildVersionOptions = {}): BuildVersionWatch {
  const running = typeof document === 'undefined' ? null : runningBundleHash()
  const watch = createBuildVersionWatch(running, opts)
  let timer: ReturnType<typeof setInterval> | null = null
  const onVisibility = () => { if (document.visibilityState === 'visible') void watch.check(true) }
  onMounted(() => {
    timer = setInterval(() => { void watch.check(false) }, opts.intervalMs ?? DEFAULT_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibility)
  })
  onUnmounted(() => {
    if (timer) clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibility)
  })
  return watch
}
